// src/pages/ClaimDossier/modals/CreateClaimDossierModal.tsx
// Modal "Tạo Hồ sơ Kết quả Trúng thưởng" — dropdown "Giải trúng"/input "Giá trị giải thưởng gốc" ở đây là
// disabled (read-only), tự động điền qua GET preview ngay khi nhập đủ Kỳ vé + Ký hiệu vé + Số vé — server vẫn
// tự chấm vé + tự tính Thuế TNCN khi submit thật (POST), không nhận prizeTierCode/prizeAmount từ client,
// xem .features/tham-dinh-ho-so/implementation-context_be.md mục 6.
import React, { useEffect, useRef, useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import classnames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  getIssuancePlansApi,
  getIssuancePlanIdsWithDrawResultApi,
  previewClaimDossierApi,
  uploadFileToStorage,
  scanCccdImageApi,
} from 'helpers/fakebackend_helper';
import { createClaimDossier } from 'slices/claimDossier/thunk';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { CreateClaimDossierPayload, IssuancePlanOption, ClaimDossierPreview, CccdOcrResult } from '../types';
import { TaxConfig, DEFAULT_TAX_CONFIG, formatTaxRate } from '../useTaxConfig';
import TicketQrScanControl from '../components/TicketQrScanControl';
import { ScannedTicket } from 'helpers/ticketQrDecode';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('claim_dossier');

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  // Mục 7.1 — tỷ lệ khấu trừ thuế TNCN động (từ system-service), chỉ dùng cho nhãn hiển thị.
  taxConfig?: TaxConfig;
}

const emptyForm = {
  issuancePlanId: '',
  symbol: '',
  ticketNumber: '',
  claimantName: '',
  claimantIdNumber: '',
  bankName: '',
  bankAccountName: '',
  bankAccountNumber: '',
};

const FILE_STORAGE_SCREEN_NAME = 'PrizeClaimDossier';

const DEFAULT_DIGIT_COUNT = 6;
// Có giải chỉ yêu cầu khớp 2-3 chữ số cuối nên cho phép nhập Số vé ngắn hơn đủ độ dài (tối thiểu 2 chữ số)
const MIN_TICKET_NUMBER_DIGITS = 2;

const formatDate = (value?: string | null) => (value ? new Date(value).toLocaleDateString('vi-VN') : '');

const CreateClaimDossierModal: React.FC<Props> = ({ isOpen, onClose, onSuccess, taxConfig = DEFAULT_TAX_CONFIG }) => {
  const location = useLocation();
  const dispatch = useDispatch<any>();
  const submitting = useSelector((state: any) => state.ClaimDossier.submitting);

  const [plans, setPlans] = useState<IssuancePlanOption[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'ticket' | 'personal'>('ticket');

  const [preview, setPreview] = useState<ClaimDossierPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Ảnh CCCD: preview (blob URL local) + URL đã upload lên FileStorage (system-service) — Create chỉ gửi URL,
  // không gửi lại file. frontScan* theo dõi riêng bước OCR tự động điền Họ và tên/Số CCCD từ ảnh mặt trước.
  const [frontImagePreview, setFrontImagePreview] = useState<string | null>(null);
  const [backImagePreview, setBackImagePreview] = useState<string | null>(null);
  const [idCardFrontImageUrl, setIdCardFrontImageUrl] = useState<string | null>(null);
  const [idCardBackImageUrl, setIdCardBackImageUrl] = useState<string | null>(null);
  const [uploadingFront, setUploadingFront] = useState(false);
  const [uploadingBack, setUploadingBack] = useState(false);
  const [scanningFront, setScanningFront] = useState(false);
  const [scanNotice, setScanNotice] = useState<string | null>(null);
  // Toàn bộ text OCR đọc được — hiển thị để nhân viên tự đối chiếu/copy khi phần tách Họ tên/Số CCCD sai.
  const [scanRawText, setScanRawText] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  // Ghi nhớ giá trị OCR đã tự điền lần gần nhất — dùng để phân biệt "còn nguyên giá trị OCR cũ" (cho phép quét
  // lại ghi đè khi upload ảnh khác) với "nhân viên đã tự sửa tay" (không ghi đè). Nếu chỉ kiểm tra "ô đang
  // trống" như trước, lần quét thứ 2 trở đi sẽ không bao giờ cập nhật vì ô đã có giá trị từ lần quét đầu.
  const [ocrFilledName, setOcrFilledName] = useState<string | null>(null);
  const [ocrFilledIdNumber, setOcrFilledIdNumber] = useState<string | null>(null);
  // Quét QR vé: đổi Kỳ vé sẽ kích hoạt effect reset Ký hiệu/Số vé bên dưới — giữ tạm ký hiệu + số vé vừa
  // quét ở ref để effect đó điền lại ngay sau khi đổi kỳ thay vì xoá trắng.
  const pendingScanRef = useRef<{ planId: string; symbol: string; ticketNumber: string } | null>(null);
  const [qrNotice, setQrNotice] = useState<string | null>(null);

  // Nạp danh sách Kỳ vé + reset form mỗi lần mở modal
  useEffect(() => {
    if (!isOpen) return;
    setForm(emptyForm);
    setError(null);
    setResult(null);
    setActiveTab('ticket');
    setPreview(null);
    setPreviewError(null);
    setFrontImagePreview(null);
    setBackImagePreview(null);
    setIdCardFrontImageUrl(null);
    setIdCardBackImageUrl(null);
    setScanNotice(null);
    setScanRawText(null);
    setImageError(null);
    setOcrFilledName(null);
    setOcrFilledIdNumber(null);
    setQrNotice(null);
    pendingScanRef.current = null;
    setLoadingPlans(true);
    // Chỉ cho chọn Kỳ vé đã có kết quả quay số thật (payout-service) — nếu không thì hệ thống không có gì để
    // đối chiếu số vé (preview/tạo hồ sơ sẽ luôn báo "không khớp giải nào").
    Promise.all([
      getIssuancePlansApi({ PageIndex: 1, PageSize: 20 }),
      getIssuancePlanIdsWithDrawResultApi(),
    ])
      .then(([plansResponse, resultIdsResponse]: any[]) => {
        const payload = plansResponse?.data || plansResponse;
        const items: IssuancePlanOption[] = payload?.items || payload?.Items || payload || [];
        const resultIds: number[] = resultIdsResponse?.data || resultIdsResponse || [];
        const resultIdSet = new Set(resultIds.map((id) => String(id)));
        setPlans(items.filter((p) => resultIdSet.has(String(p.id))));
      })
      .catch(() => setPlans([]))
      .finally(() => setLoadingPlans(false));
  }, [isOpen]);

  const selectedPlan = plans.find((p) => String(p.id) === String(form.issuancePlanId));
  const availableSymbols = selectedPlan?.ticketSymbols || [];
  // Ký hiệu đọc từ QR có thể chưa nằm trong danh sách ký hiệu của kỳ — vẫn hiển thị để nhân viên thấy đúng
  // giá trị trên vé (server là chốt chặn cuối khi chấm vé).
  const symbolOptions =
    form.symbol && !availableSymbols.includes(form.symbol) ? [...availableSymbols, form.symbol] : availableSymbols;
  // Ưu tiên độ dài dải số của chính Kỳ vé đang chọn — chỉ fallback về hằng số mặc định khi kỳ chưa có dữ liệu
  // dải số (xem implementation-context-fe.md mục 4.2)
  const digitCount = Math.max(
    selectedPlan?.numberRangeStart?.length || DEFAULT_DIGIT_COUNT,
    MIN_TICKET_NUMBER_DIGITS
  );

  // Mục 7 (README.md §7) — chỉ BẮT BUỘC thu thập thông tin cá nhân khi "Giá trị giải thưởng gốc"
  // (preview.prizeAmount) VƯỢT ngưỡng cấu hình. Khi chưa đối chiếu xong (chưa có preview) thì tạm coi
  // là bắt buộc để không cho submit thiếu; server vẫn là chốt chặn cuối theo giá trị thực.
  const requirePersonalInfo =
    !preview || Number(preview.prizeAmount) > Number(taxConfig.personalInfoThreshold);
  const personalInfoMark = requirePersonalInfo ? (
    <span className="text-danger">*</span>
  ) : (
    <span className="text-muted fs-12 ms-1">{st('khong_bat_buoc_text')}</span>
  );

  // Tab "Thông tin cá nhân" luôn hiển thị; khi giá trị giải gốc ≤ ngưỡng thì các trường chỉ ghi
  // "(không bắt buộc)" (xem personalInfoMark) thay vì ẩn cả tab.

  // Đổi Kỳ vé -> Ký hiệu vé/độ dài Số vé đổi theo -> reset để tránh giữ giá trị sai của kỳ trước
  useEffect(() => {
    const pending = pendingScanRef.current;
    if (pending && pending.planId === String(form.issuancePlanId)) {
      pendingScanRef.current = null;
      setForm((prev) => ({ ...prev, symbol: pending.symbol, ticketNumber: pending.ticketNumber }));
      return;
    }
    setForm((prev) => ({
      ...prev,
      symbol: availableSymbols.includes(prev.symbol) ? prev.symbol : availableSymbols[0] || '',
      ticketNumber: '',
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.issuancePlanId]);

  // Quét QR vé -> điền Kỳ vé + Ký hiệu + Số vé; effect preview bên dưới tự chấm vé (trúng giải nào) +
  // kiểm tra thời hạn trả thưởng.
  const handleTicketDecoded = (ticket: ScannedTicket) => {
    setError(null);
    const planId = String(ticket.lotteryPeriodId);
    const plan = plans.find((p) => String(p.id) === planId);
    if (!plan) {
      setQrNotice(null);
      setError(
        st('ky_cua_ve_chua_co_ket_qua_text', { value: ticket.planCode || `#${ticket.lotteryPeriodId}` })
      );
      return;
    }
    setActiveTab('ticket');
    if (planId === String(form.issuancePlanId)) {
      setForm((prev) => ({ ...prev, symbol: ticket.symbol, ticketNumber: ticket.ticketNumber }));
    } else {
      pendingScanRef.current = { planId, symbol: ticket.symbol, ticketNumber: ticket.ticketNumber };
      setForm((prev) => ({ ...prev, issuancePlanId: planId }));
    }
    setQrNotice(
      st('da_dien_thong_tin_ve_tu_qr_text', {
        plan: plan.planCode,
        symbol: ticket.symbol,
        ticket_number: ticket.ticketNumber,
      })
    );
  };

  const handleTicketNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, ticketNumber: e.target.value.replace(/\D/g, '').slice(0, digitCount) }));
  };

  // Ảnh mặt trước: upload lên FileStorage + tự động quét OCR (song song) để gợi ý điền sẵn Họ và tên/Số CCCD —
  // CHỈ điền vào ô đang trống, không ghi đè giá trị nhân viên đã tự nhập tay.
  const handleFrontImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageError(null);
    setScanNotice(null);
    setFrontImagePreview(URL.createObjectURL(file));
    setIdCardFrontImageUrl(null);

    setUploadingFront(true);
    uploadFileToStorage(FILE_STORAGE_SCREEN_NAME, file)
      .then((response: any) => {
        const data = response?.data || response;
        setIdCardFrontImageUrl(data?.url || null);
      })
      .catch(() => setImageError(st('tai_anh_mat_truoc_that_bai_text')))
      .finally(() => setUploadingFront(false));

    setScanningFront(true);
    setScanRawText(null);
    scanCccdImageApi(file)
      .then((response: any) => {
        const ocr: CccdOcrResult = response?.data || response;
        setScanRawText(ocr.rawText?.trim() || null);
        setForm((prev) => {
          const next = { ...prev };
          const filled: string[] = [];

          // Ghi đè khi ô đang trống HOẶC vẫn còn đúng giá trị OCR đã điền lần trước (chưa bị sửa tay) — cho
          // phép quét lại (upload ảnh khác) cập nhật được, nhưng không đụng vào giá trị nhân viên tự nhập.
          const nameUntouched = !prev.claimantName.trim() || prev.claimantName === ocrFilledName;
          if (ocr.fullName && nameUntouched) {
            next.claimantName = ocr.fullName;
            filled.push(st('ho_va_ten_text'));
          }

          const idUntouched = !prev.claimantIdNumber.trim() || prev.claimantIdNumber === ocrFilledIdNumber;
          if (ocr.idNumber && idUntouched) {
            next.claimantIdNumber = ocr.idNumber;
            filled.push(st('so_cccd_text'));
          }

          setScanNotice(
            filled.length > 0
              ? st('da_tu_dong_dien_value_tu_text', { value: filled.join(', ') })
              : ocr.fullName || ocr.idNumber
              ? st('khong_ghi_de_vi_ban_da_text')
              : st('khong_doc_duoc_thong_tin_tu_text')
          );

          return next;
        });
        // Chỉ cập nhật marker khi OCR đọc được — giữ nguyên marker cũ nếu lần quét này không đọc được field đó,
        // để không "khoá" nhầm field vẫn chưa bị sửa tay.
        if (ocr.fullName) setOcrFilledName(ocr.fullName);
        if (ocr.idNumber) setOcrFilledIdNumber(ocr.idNumber);
      })
      .catch(() => setScanNotice(st('khong_the_quet_anh_tu_dong_text')))
      .finally(() => setScanningFront(false));
  };

  const handleBackImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageError(null);
    setBackImagePreview(URL.createObjectURL(file));
    setIdCardBackImageUrl(null);

    setUploadingBack(true);
    uploadFileToStorage(FILE_STORAGE_SCREEN_NAME, file)
      .then((response: any) => {
        const data = response?.data || response;
        setIdCardBackImageUrl(data?.url || null);
      })
      .catch(() => setImageError(st('tai_anh_mat_sau_that_bai_text')))
      .finally(() => setUploadingBack(false));
  };

  // Tự động gọi GET preview (dry-run, không lưu DB) khi số vé có ít nhất 2 chữ số —
  // các hạng giải sẽ tự đối chiếu theo MatchingRule/MatchingDigits đã cấu hình.
  // debounce 400ms để tránh gọi API liên tục khi đang gõ số vé.
  useEffect(() => {
    setPreview(null);
    setPreviewError(null);

    if (
      !form.issuancePlanId ||
      !form.symbol ||
      form.ticketNumber.length < MIN_TICKET_NUMBER_DIGITS ||
      form.ticketNumber.length > digitCount
    ) {
      return;
    }

    setPreviewLoading(true);
    const timer = setTimeout(() => {
      previewClaimDossierApi({
        issuancePlanId: Number(form.issuancePlanId),
        ticketNumber: form.ticketNumber,
      })
        .then((response: any) => {
          setPreview(response?.data || response);
        })
        .catch((err: any) => {
          setPreviewError(err?.response?.data?.message || st('so_ve_khong_khop_voi_bat_text'));
        })
        .finally(() => setPreviewLoading(false));
    }, 400);

    return () => {
      clearTimeout(timer);
      setPreviewLoading(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.issuancePlanId, form.symbol, form.ticketNumber, digitCount]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!form.issuancePlanId) {
      setActiveTab('ticket');
      setError(st('vui_long_chon_ky_ve_text'));
      return;
    }
    if (!form.symbol) {
      setActiveTab('ticket');
      setError(st('vui_long_chon_ky_hieu_ve_text'));
      return;
    }
    if (
      form.ticketNumber.length < MIN_TICKET_NUMBER_DIGITS ||
      form.ticketNumber.length > digitCount
    ) {
      setActiveTab('ticket');
      setError(st('so_ve_phai_gom_tu_min_text', { min_ticket_number_digits: MIN_TICKET_NUMBER_DIGITS, digit_count: digitCount }));
      return;
    }
    // Mục 7 (README.md §7) — chỉ validate bắt buộc nhóm thông tin cá nhân khi giá trị giải thưởng gốc
    // vượt ngưỡng cấu hình. Dưới/bằng ngưỡng: cho để trống, nhưng nếu có nhập CCCD vẫn phải đúng
    // định dạng và phải chờ ảnh (nếu đang) tải xong.
    if (requirePersonalInfo) {
      if (!form.claimantName.trim()) {
        setActiveTab('personal');
        setError(st('vui_long_nhap_ho_va_ten_text'));
        return;
      }
      if (!/^[0-9]{12}$/.test(form.claimantIdNumber)) {
        setActiveTab('personal');
        setError(st('so_cccd_phai_gom_dung_12_text'));
        return;
      }
      if (!form.bankName.trim()) {
        setActiveTab('personal');
        setError(st('vui_long_nhap_ten_ngan_hang_text'));
        return;
      }
      if (!form.bankAccountName.trim()) {
        setActiveTab('personal');
        setError(st('vui_long_nhap_ten_tai_khoan_text'));
        return;
      }
      if (!form.bankAccountNumber.trim()) {
        setActiveTab('personal');
        setError(st('vui_long_nhap_so_tai_khoan_text'));
        return;
      }
      if (!idCardFrontImageUrl) {
        setActiveTab('personal');
        setError(st('vui_long_chon_anh_cccd_mat_text'));
        return;
      }
      if (!idCardBackImageUrl) {
        setActiveTab('personal');
        setError(st('vui_long_chon_anh_cccd_mat_text_2'));
        return;
      }
    } else if (form.claimantIdNumber && !/^[0-9]{12}$/.test(form.claimantIdNumber)) {
      setActiveTab('personal');
      setError(st('so_cccd_phai_gom_dung_12_text'));
      return;
    }
    // Thời hạn trả thưởng hợp lệ — preview đã báo quá hạn thì chặn luôn (server cũng chặn khi lưu).
    if (preview?.isClaimExpired) {
      setActiveTab('ticket');
      setError(
        st('qua_thoi_han_tra_thuong_text', {
          draw_date: formatDate(preview.drawDate),
          days: preview.claimValidDays,
          deadline: formatDate(preview.claimDeadline),
        })
      );
      return;
    }
    if (uploadingFront || uploadingBack) {
      setActiveTab('personal');
      setError(st('anh_cccd_dang_tai_len_vui_text'));
      return;
    }

    const payload: CreateClaimDossierPayload = {
      issuancePlanId: Number(form.issuancePlanId),
      symbol: form.symbol,
      ticketNumber: form.ticketNumber,
      claimantName: form.claimantName.trim() || undefined,
      claimantIdNumber: form.claimantIdNumber || undefined,
      bankName: form.bankName.trim() || undefined,
      bankAccountName: form.bankAccountName.trim() || undefined,
      bankAccountNumber: form.bankAccountNumber.trim() || undefined,
      idCardFrontImageUrl: idCardFrontImageUrl || undefined,
      idCardBackImageUrl: idCardBackImageUrl || undefined,
    };

    const action: any = await dispatch(createClaimDossier(payload));
    if (createClaimDossier.fulfilled.match(action)) {
      setResult(action.payload);
      onSuccess();
    } else if (typeof action.payload === 'string') {
      setError(action.payload);
    }
  };

  const handleClose = () => {
    setResult(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size="lg">
      <ModalHeader toggle={handleClose}>
        <i className="ri-file-add-line me-2"></i>{st('tao_ho_so_ket_qua_trung_title')}
      </ModalHeader>

      {result ? (
        <>
          <ModalBody>
            <div className="alert alert-success">
              <h5 className="alert-heading mb-1">{st('tao_ho_so_thanh_cong_title')} {result.dossierCode}</h5>
              <p className="mb-0">{st('he_thong_da_tu_dong_doi_text')}</p>
            </div>
            <div className="row g-2 text-center">
              <div className="col-4">
                <div className="p-2 rounded border">
                  <p className="fs-11 fw-bold text-muted mb-1">{st('giai_trung_text_2')}</p>
                  <p className="fw-bold mb-1">{result.prizeTierDisplayName || result.prizeTierCode}</p>
                  <p className="fw-bold mb-0">
                    {Number(result.prizeAmount).toLocaleString('vi-VN')} {st('d_text')}
                  </p>
                </div>
              </div>
              <div className="col-4">
                <div className="p-2 rounded border">
                  <p className="fs-11 fw-bold text-muted mb-1">{st('thue_tncn_column')}{formatTaxRate(taxConfig)})</p>
                  <p className="fw-bold mt-3 mb-0">
                    -{Number(result.withholdingTax).toLocaleString('vi-VN')} {st('d_text')}
                  </p>
                </div>
              </div>
              <div className="col-4">
                <div className="p-2 rounded border">
                  <p className="fs-11 fw-bold text-muted mb-1">{st('thuc_linh_column')}</p>
                  <p className="fw-bold mt-3 mb-0">
                    {Number(result.netAmount).toLocaleString('vi-VN')} {st('d_text')}
                  </p>
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={handleClose}>
              {st('dong_button')}
            </Button>
          </ModalFooter>
        </>
      ) : (
        <form onSubmit={handleSubmit}>
          <ModalBody>
            {error && <div className="alert alert-danger py-2">{error}</div>}

            {(
              <Nav tabs className="nav-tabs-custom mb-3">
                <NavItem>
                  <NavLink
                    href="#"
                    className={classnames({ active: activeTab === 'ticket' })}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab('ticket');
                    }}
                  >
                    <i className="ri-ticket-2-line me-1"></i>
                    {st('thong_tin_ve_text')}
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    href="#"
                    className={classnames({ active: activeTab === 'personal' })}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab('personal');
                    }}
                  >
                    <i className="ri-user-line me-1"></i>
                    {st('thong_tin_ca_nhan_text')}
                  </NavLink>
                </NavItem>
              </Nav>
            )}

            <TabContent activeTab={activeTab}>
              <TabPane tabId="ticket">
                <div className="mb-3 p-2 rounded border border-dashed bg-light-subtle">
                  <Label className="mb-1">
                    <i className="ri-qr-code-line me-1"></i>
                    {st('quet_qr_ve_de_dien_tu_dong_label')}
                  </Label>
                  <TicketQrScanControl
                    onDecoded={handleTicketDecoded}
                    disabled={loadingPlans || !PageActionPermission.canEnable(location.pathname, 'input-ticket-number')}
                  />
                  {qrNotice && (
                    <div className="text-success fs-13 mt-1">
                      <i className="ri-checkbox-circle-line align-bottom me-1"></i>
                      {qrNotice}
                    </div>
                  )}
                </div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <FormGroup className="mb-0">
                      <Label>
                        {st('ky_ve_text')} <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="select"
                        value={form.issuancePlanId}
                        disabled={
                          loadingPlans ||
                          !PageActionPermission.canEnable(location.pathname, 'input-issuance-plan')
                        }
                        data-action-code="input-issuance-plan"
                        data-action-name="Kỳ vé"
                        onChange={(e) => setForm({ ...form, issuancePlanId: e.target.value })}
                      >
                        <option value="">
                          {loadingPlans ? st('dang_tai_text') : plans.length === 0 ? st('chua_co_ky_nao_co_ket_text') : st('chon_ky_ve_text')}
                        </option>
                        {plans.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.planCode}
                          </option>
                        ))}
                      </Input>
                      <small className="text-muted">{st('chi_hien_thi_cac_ky_da_hint')}</small>
                    </FormGroup>
                  </div>
                  <div className="col-md-6">
                    <FormGroup className="mb-0">
                      <Label>
                        {st('ky_hieu_ve_label')} <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="select"
                        value={form.symbol}
                        disabled={
                          !selectedPlan ||
                          !PageActionPermission.canEnable(location.pathname, 'input-symbol')
                        }
                        data-action-code="input-symbol"
                        data-action-name="Ký hiệu vé"
                        onChange={(e) => setForm({ ...form, symbol: e.target.value })}
                      >
                        <option value="">{st('chon_ky_hieu_option')}</option>
                        {symbolOptions.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </div>
                  <div className="col-md-6">
                    <FormGroup className="mb-0">
                      <Label>
                        {st('so_ve_trung_thuong_min_ticket_text', { min_ticket_number_digits: MIN_TICKET_NUMBER_DIGITS, digit_count: digitCount })}{' '}
                        <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        value={form.ticketNumber}
                        maxLength={digitCount}
                        placeholder={st('nhap_tu_min_ticket_number_digits_text', { min_ticket_number_digits: MIN_TICKET_NUMBER_DIGITS, digit_count: digitCount })}
                        data-action-code="input-ticket-number"
                        data-action-name="Số vé trúng thưởng"
                        onChange={handleTicketNumberChange}
                        disabled={
                          !selectedPlan ||
                          !PageActionPermission.canEnable(location.pathname, 'input-ticket-number')
                        }
                      />
                      <small className="text-muted">
                        {st('co_the_nhap_so_ngan_tu_hint')}
                      </small>
                    </FormGroup>
                  </div>
                  <div className="col-md-6">
                    <FormGroup className="mb-0">
                      <Label>{st('giai_trung_text')}</Label>
                      <Input
                        type="text"
                        disabled
                        value={
                          previewLoading
                            ? st('dang_doi_chieu_text')
                            : preview
                            ? preview.prizes.map((p) => p.prizeTierDisplayName).join(', ')
                            : ''
                        }
                      />
                    </FormGroup>
                  </div>
                  <div className="col-md-6">
                    <FormGroup className="mb-0">
                      <Label>{st('gia_tri_giai_thuong_goc_vnd_label')}</Label>
                      <Input
                        type="text"
                        disabled
                        value={preview ? st('value_d_text', { value: Number(preview.prizeAmount).toLocaleString('vi-VN') }) : ''}
                      />
                    </FormGroup>
                  </div>
                </div>

                {previewError && (
                  <div className="text-danger fs-13 mt-2">
                    <i className="ri-error-warning-line align-bottom me-1"></i>
                    {previewError}
                  </div>
                )}

                {/* Kết quả chấm vé + thời hạn trả thưởng hợp lệ (ngày mở thưởng in trên vé + N ngày cấu hình) */}
                {preview && (
                  <div className={`alert ${preview.isClaimExpired ? 'alert-danger' : 'alert-success'} py-2 mt-2 mb-0 fs-13`}>
                    <i className={`${preview.isClaimExpired ? 'ri-time-line' : 'ri-trophy-line'} align-bottom me-1`}></i>
                    {preview.isClaimExpired
                      ? st('qua_thoi_han_tra_thuong_text', {
                          draw_date: formatDate(preview.drawDate),
                          days: preview.claimValidDays,
                          deadline: formatDate(preview.claimDeadline),
                        })
                      : st('ve_trung_thuong_con_han_text', {
                          draw_date: formatDate(preview.drawDate),
                          deadline: formatDate(preview.claimDeadline),
                        })}
                  </div>
                )}

                {/* Vé có thể trúng nhiều giải cùng lúc (VD số toàn 0 khớp cả Đặc biệt lẫn các giải an ủi) — liệt
                    kê chi tiết từng giải để nhân viên đối chiếu, "Giá trị giải thưởng gốc" ở trên là tổng các
                    dòng này. */}
                {preview && preview.prizes.length > 1 && (
                  <div className="border rounded p-2 mt-2">
                    <table className="table table-sm table-borderless mb-0">
                      <tbody>
                        {preview.prizes.map((p) => (
                          <tr key={p.prizeTierCode}>
                            <td className="py-1 ps-1">{p.prizeTierDisplayName}</td>
                            <td className="py-1 pe-1 text-end">
                              {Number(p.prizeAmount).toLocaleString('vi-VN')} {st('d_text')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {preview && (
                  <div className="row g-2 text-center mt-1">
                    <div className="col-4">
                      <div className="p-2 rounded border">
                        <p className="fs-11 fw-bold text-muted mb-1">{st('tien_thuong_text_2')}</p>
                        <p className="fw-bold mb-0">{Number(preview.prizeAmount).toLocaleString('vi-VN')} {st('d_text')}</p>
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="p-2 rounded border">
                        <p className="fs-11 fw-bold text-muted mb-1">{st('thue_tncn_column')}{formatTaxRate(taxConfig)})</p>
                        <p className="fw-bold mb-0">
                          -{Number(preview.withholdingTax).toLocaleString('vi-VN')} {st('d_text')}
                        </p>
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="p-2 rounded border">
                        <p className="fs-11 fw-bold text-muted mb-1">{st('thuc_linh_column')}</p>
                        <p className="fw-bold mb-0">{Number(preview.netAmount).toLocaleString('vi-VN')} {st('d_text')}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="form-text mt-2">
                  <i className="ri-information-line me-1"></i>
                  {st('he_thong_se_tu_dong_doi_text')}
                </div>
              </TabPane>

              {(
              <TabPane tabId="personal">
                <div className="row g-3">
                  <div className="col-md-6">
                    <FormGroup className="mb-0">
                      <Label>
                        {st('so_cccd_text')} {personalInfoMark}
                      </Label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        maxLength={12}
                        value={form.claimantIdNumber}
                        placeholder="0123456789"
                        data-action-code="input-claimant-id-number"
                        data-action-name="Số CCCD người lĩnh"
                        onChange={(e) => setForm({ ...form, claimantIdNumber: e.target.value.replace(/\D/g, '') })}
                        disabled={!PageActionPermission.canEnable(location.pathname, 'input-claimant-id-number')}
                      />
                    </FormGroup>
                  </div>
                  <div className="col-md-6">
                    <FormGroup className="mb-0">
                      <Label>
                        {st('ho_va_ten_nguoi_linh_label')} {personalInfoMark}
                      </Label>
                      <Input
                        type="text"
                        value={form.claimantName}
                        placeholder={st('nguyen_van_a_placeholder')}
                        data-action-code="input-claimant-name"
                        data-action-name="Họ và tên người lĩnh"
                        onChange={(e) => setForm({ ...form, claimantName: e.target.value })}
                        disabled={!PageActionPermission.canEnable(location.pathname, 'input-claimant-name')}
                      />
                    </FormGroup>
                  </div>

                  <div className="col-md-6">
                    <FormGroup className="mb-0">
                      <Label>
                        {st('anh_cccd_mat_truoc_alt')} {personalInfoMark}
                      </Label>
                      <Input
                        type="file"
                        accept="image/*"
                        data-action-code="input-id-card-front-image"
                        data-action-name="Ảnh CCCD mặt trước"
                        onChange={handleFrontImageChange}
                        disabled={!PageActionPermission.canEnable(location.pathname, 'input-id-card-front-image')}
                      />
                      {frontImagePreview && (
                        <div className="mt-2 d-flex align-items-center gap-2">
                          <img
                            src={frontImagePreview}
                            alt={st('anh_cccd_mat_truoc_alt')}
                            className="rounded border"
                            style={{ height: 70, objectFit: 'cover' }}
                          />
                          {(uploadingFront || scanningFront) && (
                            <span className="text-muted fs-12">
                              <span className="spinner-border spinner-border-sm me-1" />
                              {uploadingFront && scanningFront
                                ? st('dang_tai_anh_quet_thong_tin_text')
                                : uploadingFront
                                ? st('dang_tai_anh_text')
                                : st('dang_quet_thong_tin_text')}
                            </span>
                          )}
                          {!uploadingFront && idCardFrontImageUrl && (
                            <span className="text-success fs-12">
                              <i className="ri-checkbox-circle-fill align-bottom me-1"></i>{st('da_tai_len_text')}
                            </span>
                          )}
                        </div>
                      )}
                      {scanNotice && !scanningFront && <div className="form-text mb-0 mt-1">{scanNotice}</div>}
                      {scanRawText && !scanningFront && (
                        <details className="mt-1">
                          <summary className="form-text mb-0" style={{ cursor: 'pointer' }}>
                            {st('xem_chu_doc_duoc_tu_anh_text')}
                          </summary>
                          <pre
                            className="form-text mb-0 mt-1 p-2 bg-light border rounded"
                            style={{ whiteSpace: 'pre-wrap', maxHeight: 160, overflowY: 'auto', fontSize: 12 }}
                          >
                            {scanRawText}
                          </pre>
                        </details>
                      )}
                    </FormGroup>
                  </div>
                  <div className="col-md-6">
                    <FormGroup className="mb-0">
                      <Label>
                        {st('anh_cccd_mat_sau_alt')} {personalInfoMark}
                      </Label>
                      <Input
                        type="file"
                        accept="image/*"
                        data-action-code="input-id-card-back-image"
                        data-action-name="Ảnh CCCD mặt sau"
                        onChange={handleBackImageChange}
                        disabled={!PageActionPermission.canEnable(location.pathname, 'input-id-card-back-image')}
                      />
                      {backImagePreview && (
                        <div className="mt-2 d-flex align-items-center gap-2">
                          <img
                            src={backImagePreview}
                            alt={st('anh_cccd_mat_sau_alt')}
                            className="rounded border"
                            style={{ height: 70, objectFit: 'cover' }}
                          />
                          {uploadingBack && (
                            <span className="text-muted fs-12">
                              <span className="spinner-border spinner-border-sm me-1" />
                              {st('dang_tai_anh_text')}
                            </span>
                          )}
                          {!uploadingBack && idCardBackImageUrl && (
                            <span className="text-success fs-12">
                              <i className="ri-checkbox-circle-fill align-bottom me-1"></i>{st('da_tai_len_text')}
                            </span>
                          )}
                        </div>
                      )}
                    </FormGroup>
                  </div>
                  {imageError && (
                    <div className="col-12">
                      <div className="text-danger fs-13">
                        <i className="ri-error-warning-line align-bottom me-1"></i>
                        {imageError}
                      </div>
                    </div>
                  )}

                  <div className="col-md-6">
                    <FormGroup className="mb-0">
                      <Label>
                        {st('ten_ngan_hang_label')} {personalInfoMark}
                      </Label>
                      <Input
                        type="text"
                        value={form.bankName}
                        placeholder={st('vietcombank_placeholder')}
                        data-action-code="input-bank-name"
                        data-action-name="Tên ngân hàng"
                        onChange={(e) => setForm({ ...form, bankName: e.target.value })}
                        disabled={!PageActionPermission.canEnable(location.pathname, 'input-bank-name')}
                      />
                    </FormGroup>
                  </div>
                  <div className="col-md-6">
                    <FormGroup className="mb-0">
                      <Label>
                        {st('ten_tai_khoan_ngan_hang_label')} {personalInfoMark}
                      </Label>
                      <Input
                        type="text"
                        value={form.bankAccountName}
                        placeholder={st('nguyen_van_a_placeholder_2')}
                        data-action-code="input-bank-account-name"
                        data-action-name="Tên tài khoản ngân hàng"
                        onChange={(e) => setForm({ ...form, bankAccountName: e.target.value })}
                        disabled={!PageActionPermission.canEnable(location.pathname, 'input-bank-account-name')}
                      />
                    </FormGroup>
                  </div>
                  <div className="col-md-6">
                    <FormGroup className="mb-0">
                      <Label>
                        {st('so_tai_khoan_label')} {personalInfoMark}
                      </Label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        value={form.bankAccountNumber}
                        placeholder="0123456789"
                        data-action-code="input-bank-account-number"
                        data-action-name="Số tài khoản ngân hàng"
                        onChange={(e) => setForm({ ...form, bankAccountNumber: e.target.value.replace(/\D/g, '') })}
                        disabled={!PageActionPermission.canEnable(location.pathname, 'input-bank-account-number')}
                      />
                    </FormGroup>
                  </div>
                </div>
              </TabPane>
              )}
            </TabContent>
          </ModalBody>
          <ModalFooter>
            <Button color="light" type="button" onClick={handleClose}>
              {st('huy_title')}
            </Button>
            {PageActionPermission.canDisplay(location.pathname, 'button-submit') && (
              <Button
                color="primary"
                type="submit"
                data-action-code="button-submit"
                data-action-name="Lưu hồ sơ chờ duyệt"
                disabled={submitting || !!preview?.isClaimExpired}
              >
                {submitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    {st('dang_luu_text')}
                  </>
                ) : (
                  <>
                    <i className="ri-save-line me-1"></i>{st('luu_ho_so_cho_duyet_text')}
                  </>
                )}
              </Button>
            )}
          </ModalFooter>
        </form>
      )}
    </Modal>
  );
};

export default CreateClaimDossierModal;
